import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const dialogTheme = createTheme({
  direction: 'rtl',
});

export const ConfigDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  tabs,
  isLoading = false,
  confirmDisabled = false,
  confirmText = "שמור",
  cancelText = "ביטול",
  confirmButtonColor,
  width = 500
}) => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open) setActiveTab(0);
  }, [open]);

  return (
    <ThemeProvider theme={dialogTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: width },
            maxWidth: '96vw',
            maxHeight: '92vh',
            bgcolor: '#ffffff',
            borderRadius: '16px',
            m: { xs: 1, sm: 2 },
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '92vh' }}>

          {/* Header */}
          <Box sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid rgba(30,58,95,0.08)',
            background: 'linear-gradient(135deg, rgba(30,58,95,0.03) 0%, transparent 80%)',
          }}>
            <Typography sx={{ color: '#1e3a5f', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
              {title}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: '#6b7280',
                bgcolor: 'rgba(30,58,95,0.05)',
                borderRadius: '8px',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'rgba(30,58,95,0.10)', color: '#1e3a5f' }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Tabs */}
          {tabs && tabs.length > 0 && (
            <Box sx={{ px: 3, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 44,
                  '& .MuiTab-root': {
                    fontWeight: 500,
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    minHeight: 44,
                    px: 1.5,
                    py: 0,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': { color: '#1e3a5f', fontWeight: 600 },
                  '& .MuiTabs-indicator': { backgroundColor: '#1e3a5f', height: '2px', borderRadius: '2px 2px 0 0' },
                  '& .MuiTabs-scrollButtons': { color: '#9ca3af' },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>
            </Box>
          )}

          {/* Content */}
          <Box sx={{
            p: 3,
            flexGrow: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#e5e7eb', borderRadius: 3 },
          }}>
            {tabs && tabs.length > 0 ? tabs[activeTab].content : children}
          </Box>

          {/* Footer */}
          <Box sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(30,58,95,0.08)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            flexShrink: 0,
            bgcolor: 'rgba(30,58,95,0.02)',
          }}>
            <Button
              onClick={onClose}
              sx={{
                color: '#5a6978',
                fontWeight: 500,
                fontSize: '0.875rem',
                px: 2.5,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(30,58,95,0.06)', color: '#1e3a5f' }
              }}
            >
              {cancelText}
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={isLoading || confirmDisabled}
              sx={{
                bgcolor: confirmButtonColor || '#1e3a5f',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 3,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(30,58,95,0.2)',
                transition: 'box-shadow 0.2s, background-color 0.2s',
                '&:hover': {
                  bgcolor: confirmButtonColor ? `${confirmButtonColor}dd` : '#152a47',
                  boxShadow: '0 4px 16px rgba(30,58,95,0.35)',
                },
                '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' }
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                  שומר...
                </>
              ) : confirmText}
            </Button>
          </Box>

        </Box>
      </Dialog>
    </ThemeProvider>
  );
};
